// Centralized data formatting utility
// Handles currency, dates, percentages, integers, decimals — auto-detected by column name

export type ColumnType = 'currency' | 'percent' | 'date' | 'integer' | 'decimal' | 'text';

const CURRENCY_KEYWORDS = /revenue|price|cost|amount|salary|total|fee|mrr|arr|spend|budget|sales|earning|income|value|charge|rate_dollar|ltv|cac|gmv/i;
const PERCENT_KEYWORDS = /pct|percent|rate|ratio|share|margin|growth|churn|conversion|utilization|efficiency|score/i;
const DATE_KEYWORDS = /date|time|created|updated|timestamp|at$|_at|day|month|year|period|week/i;

/**
 * Detect the semantic type of a column based on its name and sample values.
 */
export function detectColumnType(columnName: string, sampleValues: any[] = []): ColumnType {
  const name = columnName.toLowerCase();

  // Explicit date check first
  if (DATE_KEYWORDS.test(name)) {
    const sample = sampleValues.find(v => v != null && v !== '');
    if (sample && isDateLike(String(sample))) return 'date';
  }

  // Percent check before currency — so "profit_margin", "growth_rate" etc. win over partial currency matches
  if (PERCENT_KEYWORDS.test(name)) return 'percent';

  // Currency check
  if (CURRENCY_KEYWORDS.test(name)) return 'currency';

  // Numeric analysis on samples
  const numericSamples = sampleValues
    .filter(v => v != null && v !== '' && !isNaN(Number(v)))
    .map(Number);

  if (numericSamples.length > 0) {
    const hasDecimals = numericSamples.some(n => !Number.isInteger(n));
    // If percent range (0–1 or 0–100 with "pct" in name)
    if (PERCENT_KEYWORDS.test(name)) return 'percent';
    return hasDecimals ? 'decimal' : 'integer';
  }

  return 'text';
}

function isDateLike(value: string): boolean {
  if (!value || value.length < 4) return false;
  const parsed = Date.parse(value);
  return !isNaN(parsed) && parsed > 0;
}

/**
 * Format a currency value: $1,234.56
 */
export function formatCurrency(value: any, minimumFractionDigits = 0, maximumFractionDigits = 2): string {
  const num = Number(value);
  if (isNaN(num) || value == null || value === '') return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(num);
}

/**
 * Format a date value: Jan 22, 2025
 */
export function formatDate(value: any): string {
  if (value == null || value === '') return '—';
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a datetime value: Jan 22, 2025 · 2:34 PM
 */
export function formatDateTime(value: any): string {
  if (value == null || value === '') return '—';
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return String(value);
  return (
    parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) +
    ' · ' +
    parsed.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  );
}

/**
 * Format a percentage value: 12.5%
 */
export function formatPercent(value: any): string {
  const num = Number(value);
  if (isNaN(num) || value == null || value === '') return '—';
  // If value is already in 0–100 range, divide by 100 for Intl
  const normalized = Math.abs(num) <= 1 ? num : num / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(normalized);
}

/**
 * Format an integer: 1,234,567
 */
export function formatInteger(value: any): string {
  const num = Number(value);
  if (isNaN(num) || value == null || value === '') return '—';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(num);
}

/**
 * Format a decimal: 1,234.56
 */
export function formatDecimal(value: any, decimals = 2): string {
  const num = Number(value);
  if (isNaN(num) || value == null || value === '') return '—';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
}

/**
 * Master formatter — given a value and its detected column type, return a formatted string.
 */
export function formatCell(value: any, type: ColumnType): string {
  if (value == null || value === '') return '—';
  switch (type) {
    case 'currency': return formatCurrency(value);
    case 'percent': return formatPercent(value);
    case 'date': return formatDate(value);
    case 'integer': return formatInteger(value);
    case 'decimal': return formatDecimal(value);
    case 'text': default: return String(value);
  }
}

/**
 * Auto-detect and format in one call.
 */
export function autoFormat(value: any, columnName: string, sampleValues: any[] = []): string {
  const type = detectColumnType(columnName, sampleValues);
  return formatCell(value, type);
}

/**
 * Column type icon (emoji) for display in headers.
 */
export function columnTypeIcon(type: ColumnType): string {
  switch (type) {
    case 'currency': return '$';
    case 'percent': return '%';
    case 'date': return '📅';
    case 'integer': return '#';
    case 'decimal': return '0.0';
    case 'text': default: return 'Aa';
  }
}

/**
 * Format large numbers compactly: 1.2M, 34.5K
 */
export function formatCompact(value: any): string {
  const num = Number(value);
  if (isNaN(num)) return '—';
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(num);
}

/**
 * Format a positive delta with + sign: +12.5%, -3.2%
 */
export function formatDelta(value: any, type: ColumnType = 'percent'): string {
  const num = Number(value);
  if (isNaN(num)) return '—';
  const prefix = num > 0 ? '+' : '';
  return prefix + formatCell(value, type);
}
